// Copyright Epic Games, Inc. All Rights Reserved.

import { json } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";

import { isSimpleSection } from "~/typings";
import { getDictionary } from "~/utils/dictionaries";
import { localeTokenMap } from "~/utils/localeTokenMap";

import { getSdk } from "../../generated/schema.graphcms";
import { mergeMeta } from "../../metatags/mergeMeta";
import { getGraphCms, getStage } from "../../services/graphcms";

import Divbar from "./components/home/Divbar";
import HomeIntroDesktop from "./components/home/IntroDesktop";
import HomeIntroMobile from "./components/home/IntroMobile";
import LearnMore from "./components/home/LearnMore";
import PlayNowModal from "./components/home/PlayNowModal";
import Sections from "./components/home/Sections";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";

import type { Locale as AppLocale } from "~/i18n.config";
import type { SimpleSection } from "~/typings";
import type { Dictionary } from "~/utils/dictionaries";

import type { EnumBrand, GetArticlesQuery, GetPageQuery, Locale } from "../../generated/schema.graphcms";

const slug = "sideswipe-home";

export const meta: MetaFunction<typeof loader> = mergeMeta<typeof loader>(({ data }) => {
	const { page, url: requestUrl, locale } = data ?? {};

	const url = requestUrl ? new URL(requestUrl) : undefined;

	// MW-12222 will replace these inline strings with proper canonical/hreflang
	// emit. For now: clean home URL ("/"), `og:lang` reflects the resolved locale.
	const metaTags = [
		{ title: "Rocket League Sideswipe" },
		{ property: "description", content: page?.description },
		{ property: "url", content: "/" },

		{ property: "og:title", content: page?.title },
		{ property: "og:description", content: page?.description },
		{ property: "og:url", content: "/" },
		{
			property: "og:site_name",
			content: "Rocket League Sideswipe Official Website",
		},
		{ property: "og:lang", content: locale },
		{ property: "og:type", content: "website" },

		{ property: "twitter:card", content: "summary_large_image" },
		{
			property: "twitter:title",
			content: "Rocket League ® - Official Site",
		},
	];

	if (url) {
		metaTags.push({
			property: "og:image",
			content: `${url.origin}/images/RLS_KeyArt_Primary_Horiz_RGB_4K.jpg`,
		});
	} else {
		console.error("No request URL found for meta tags");
	}

	return metaTags;
});

async function getPageData(env: Env, mappedLocaleToken: string): Promise<GetPageQuery["page"]> {
	const { GetPage } = getSdk(getGraphCms(env));
	const pageData = await GetPage({
		locale: [mappedLocaleToken, "en"] as Locale[],
		slug,
		stage: getStage(env),
	});
	return pageData.page;
}

async function getArticlesData(env: Env, mappedLocaleToken: string): Promise<GetArticlesQuery> {
	const { GetArticles } = getSdk(getGraphCms(env));
	const articleData = await GetArticles({
		locale: [mappedLocaleToken, "en"] as Locale[],
		website_contains_all: ["Sideswipe", "Sideswipe"] as EnumBrand[],
		limit: 2,
		offset: 0,
		stage: getStage(env),
	});
	const articles = articleData.articles;
	return { articles };
}

export default function Home(): React.ReactNode {
	const { page, articleData, dict, locale } = useLoaderData<typeof loader>();

	const lowerSections = page?.sections.slice(1) as SimpleSection[];

	const simpleSection = page?.sections[0] as SimpleSection | undefined | null;

	return (
		<>
			<div className="is-hidden-desktop">
				<HomeIntroMobile copy={dict} tileData={articleData} />
			</div>
			<div className="is-hidden-touch">
				<HomeIntroDesktop copy={dict} tileData={articleData} />
			</div>
			<Divbar />
			{/* @TODO: should we be preventing the `null` eventuality here, and determine the section in the loader instead? */}
			{!!simpleSection && isSimpleSection(simpleSection) ? <LearnMore data={simpleSection} /> : null}
			<Divbar />
			{/* @TODO: same here as above */}
			<Sections data={lowerSections} />
			<Divbar />
			<PlayNowModal locale={locale} copy={dict} />
		</>
	);
}

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
	const { env } = context.cloudflare;
	const { locale } = context;

	const dict = await getDictionary(locale);
	const mappedLocaleToken = localeTokenMap(locale);

	const page = await getPageData(env, mappedLocaleToken);
	const articleData = await getArticlesData(env, mappedLocaleToken);

	return json<{
		page: GetPageQuery["page"];
		articleData: GetArticlesQuery;
		dict: Dictionary;
		url: string;
		locale: AppLocale;
	}>({ page, articleData, dict, url: request.url, locale });
};
